import { useState } from 'react'

export interface UserDataExport {
  user: {
    id: string
    email: string
    name: string
    role: string
    emailVerified: boolean
    createdAt: string
    lastLogin: string | null
  }
  company: {
    id: string | null
    name: string | null
    description: string | null
    website: string | null
  } | null
  activity: Array<{
    id: string
    action: string
    timestamp: string
    metadata: Record<string, any> | null
  }>
  consents: {
    privacyPolicy: boolean
    termsOfService: boolean
    marketingEmails: boolean
    consentDate: string | null
  }
}

export interface UseExportDataResult {
  exportData: () => Promise<UserDataExport | null>
  loading: boolean
  error: string | null
}

export function useExportData(): UseExportDataResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const exportData = async (): Promise<UserDataExport | null> => {
    try {
      setLoading(true)
      setError(null)

      // Get auth token
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('/api/gdpr/export', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || `Failed to export data (${response.status})`)
      }

      const data: UserDataExport = await response.json()
      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export data'
      setError(errorMessage)
      console.error('Error exporting data:', err)
      return null
    } finally {
      setLoading(false)
    }
  }

  return {
    exportData,
    loading,
    error
  }
}
